import { Location, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Params, Router, RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { SwitchProfileOverlayComponent } from '@rma/authentication/private/ui-switch-profile-overlay';
import type { AvailableProfile } from '@rma/authentication/public/user';
import { CalloutComponent } from '@rma/generic/ui/callout';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { RmaModalService } from '@rma/generic/ui/modal';
import { SkeletonBlockComponent } from '@rma/generic/ui/skeleton';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { debounceTime, delay, distinctUntilChanged, from, fromEvent, map, switchMap } from 'rxjs';
import { TRANSLATION_SCOPE } from '../domain/translation-scope';
import { NoProfilesFoundComponent } from '../ui-no-profiles/no-profiles-found.component';
import { ProfileSwitchItemComponent } from '../ui-profile-switch-item/profile-switch-item.component';
import { SwitchAccountFacade, TAKE } from './switch-account.facade';

@Component({
  selector: 'rma-orc-switch-account',
  imports: [
    CalloutComponent,
    FormsModule,
    InfiniteScrollDirective,
    LowerCasePipe,
    MatButtonModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatTooltipModule,
    NoProfilesFoundComponent,
    ProfileSwitchItemComponent,
    RmaIconComponent,
    RouterLink,
    SkeletonBlockComponent,
    TranslocoDirective,
  ],
  providers: [provideTranslocoScope(TRANSLATION_SCOPE), SwitchAccountFacade],
  templateUrl: './switch-account.component.html',
  styles: `
    @use '@angular/material' as mat;
    :host {
      @include mat.form-field-density(-3);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwitchAccountComponent {
  private readonly dialogRef = inject(MatDialogRef<SwitchAccountComponent>);
  private readonly facade = inject(SwitchAccountFacade);
  private readonly modalService = inject(RmaModalService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);

  private readonly searchTerm = viewChild<ElementRef<HTMLInputElement>>('searchTerm');

  private readonly isAgent = computed(() => this.profileType() === 'Agent');
  private readonly noResults = computed(() => this.profiles().length === 0 && !this.loading());

  public readonly currentProfile = this.facade.currentProfile;
  public readonly defaultProfile = this.facade.defaultProfile;
  public readonly hasMore = this.facade.hasMore;
  public readonly isAssisting = this.facade.isAssisting;
  public readonly loading = this.facade.loading;
  public readonly loadingError = this.facade.loadingError;
  public readonly profileType = this.facade.profileType;
  public readonly profiles = this.facade.profiles;
  public readonly searchTermValue = this.facade.searchTerm;
  public readonly selectedTabIndex = this.facade.selectedTabIndex;

  public readonly showNoFilteredProfilesCta = computed(() => this.noResults() && this.searchTermValue());
  public readonly showNoAvailableProfilesCta = computed(() => this.noResults() && !this.searchTermValue() && this.isAgent());

  public readonly scope = TRANSLATION_SCOPE;
  public readonly take = TAKE;

  constructor() {
    effect(() => {
      const searchInput = this.searchTerm()?.nativeElement;

      if (searchInput) {
        fromEvent(searchInput, 'keyup')
          .pipe(
            debounceTime(300),
            map(() => searchInput.value),
            distinctUntilChanged(),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe((input) => this.facade.setSearchTerm(input));
      }
    });
  }

  public onTabSelected(index: number) {
    this.facade.selectTab(index);
  }

  public onSwitchAccount(
    profile: AvailableProfile,
    redirectUrl?: {
      url: string[];
      queryParams?: Params;
    },
  ) {
    this.switchProfile(profile, redirectUrl);
  }

  public onStopAssisting() {
    const { name, profileCode, profileType, profileImage } = this.defaultProfile();

    if (profileType && profileCode) {
      this.switchProfile({ name, profileCode, profileType, profileImage });
    }
  }

  public onManageAccount(profile: AvailableProfile, agentName?: string) {
    const tree = ['/', 'manage-account', 'assistant-access'];
    const queryParams = { agentName };

    if (`${profile.profileType}${profile.profileCode}` !== `${this.currentProfile().profileType}${this.currentProfile().profileCode}`) {
      this.switchProfile(profile, { url: tree, queryParams });
    } else {
      this.dialogRef.close();
      this.router.navigate(tree, { queryParams });
    }
  }

  public onNavigate() {
    this.dialogRef.close();

    this.router.navigate(['/', 'manage-account', 'assistant-access']);
  }

  public loadMore() {
    this.facade.loadMore();
  }

  public onLogout(): void {
    this.facade.logout();
  }

  private switchProfile(profile: AvailableProfile, redirectUrl?: { url: string[]; queryParams?: Params } | undefined) {
    this.dialogRef.close();

    const switchProfileOverlay = this.modalService.openWithBlurBackground(SwitchProfileOverlayComponent, 'rma-dashboard', {
      data: { name: profile.name, image: profile.profileImage },
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '400ms',
      panelClass: ['switch-profile-overlay'],
      disableClose: true,
    });

    const { profileType, profileCode } = profile;
    const currentUrl = this.location.path();

    from(this.router.navigateByUrl('/switch-account', { skipLocationChange: true }))
      .pipe(switchMap(() => this.facade.switchAccount({ profileType, profileCode }).pipe(delay(1000))))
      .subscribe(() => {
        if (redirectUrl) {
          this.router.navigate(redirectUrl.url, { queryParams: redirectUrl.queryParams });
        } else {
          this.router.navigateByUrl(currentUrl);
        }
        switchProfileOverlay.close();
      });
  }
}
